import Cookie from 'js-cookie';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { LocaleCookie } from '@server/resolveLocale';

const CONVERT_COOKIES_TO_LOCAL_STORAGE_LOG = LoggerFactory.create('convertCookiesToLocalStorage');

const SHOULD_RENDER_COOKIE_BANNER_LOG = LoggerFactory.create('shouldRenderCookieBanner');

export const MAX_COOKIE_ENTRIES = 10;
/**
 * @description Takes an array of cookie key strings and checks if one of them has been
 * set
 */
export const hasCookieKey = (...cookieKeys: string[]): boolean =>
  cookieKeys.some(key => key && Boolean(Cookie.get(key)));

/**
 * @description Used to check and return bool for whether or not a value (usually eventId) is part of the array.
 */
export const checkCookieForData = (cookieName: string, cookieValue: string, matchValue: string): boolean => {
  try {
    const acceptedEventIdsData = cookieValue ? JSON.parse(cookieValue) : [];

    return acceptedEventIdsData?.length > 0 && acceptedEventIdsData.some(id => id === matchValue);
  } catch (jsonParseError) {
    // Error parsing cookie value, clear it to be safe
    Cookie.remove(cookieName);
    return false;
  }
};

export function convertArrayIntoMap<T>(eventIdsLocale: Array<T>): T {
  return eventIdsLocale.reduce((acc, curr) => {
    const { eventId = null, locale = null } = curr as unknown as { eventId: string; locale: string };
    let eventIdLocaleObject = acc;
    if (eventIdLocaleObject[eventId]) {
      eventIdLocaleObject[eventId] = locale;
    } else {
      eventIdLocaleObject = { ...acc, [eventId]: locale };
    }
    return eventIdLocaleObject;
  }, {} as T);
}

/**
 * @description Used to check and return item value if present in cookie values array which we get after parsing it from cookie.
 */
export const getCookieDataItem = <T = string | { [eventId: string]: string }>(
  cookieName: string,
  cookieValue: string,
  eventID: string
): T => {
  try {
    let hasAcceptedEventData;
    if (cookieName === 'cvt_cookielocale') {
      const acceptedEventIdsLocale = cookieValue ? JSON.parse(cookieValue) : {};
      if (acceptedEventIdsLocale?.length) {
        hasAcceptedEventData = convertArrayIntoMap<LocaleCookie>(acceptedEventIdsLocale);
        return hasAcceptedEventData;
      }
      Object.keys(acceptedEventIdsLocale).forEach((eventId: string) => {
        if (eventId === eventID) {
          hasAcceptedEventData = { [eventId]: acceptedEventIdsLocale[eventId] };
        }
      });
      if (!hasAcceptedEventData) {
        hasAcceptedEventData = false;
      }
    } else {
      const acceptedEventIdsData = cookieValue ? JSON.parse(cookieValue) : [];
      hasAcceptedEventData =
        (acceptedEventIdsData?.length > 0 && acceptedEventIdsData.find(({ eventId }) => eventId === eventID)) || false;
    }
    return hasAcceptedEventData;
  } catch (jsonParseError) {
    // Error parsing cookie value, clear it to be safe
    Cookie.remove(cookieName);
    return null;
  }
};

/**
 * @description Used check or remove if max entry exceed 10 value in cookie
 */
export const checkMaxEntries = <T = string | { eventId: string; locale: string }>(
  dataArray: Array<T>,
  appendValue: T,
  maxCookieEntries = MAX_COOKIE_ENTRIES
): Array<T> => {
  // Add eventId to start of list
  const acceptedDataArray = [appendValue, ...dataArray];
  // When running up against max entries, ditch last one(s)
  if (acceptedDataArray.length > maxCookieEntries) {
    return acceptedDataArray.slice(0, maxCookieEntries);
  }
  return acceptedDataArray;
};

/**
 * @description Used check or remove if max entry exceed 10 value in locale cookie object
 */
export function checkMaxEntriesInObject<T>(dataObject: T, appendValue: T, maxCookieEntries = MAX_COOKIE_ENTRIES): T {
  // Add eventId to start of Object
  const acceptedDataObject = { ...appendValue, ...dataObject };

  let acceptedDataKeys = Object.keys(acceptedDataObject);
  // When running up against max entries, ditch last one(s)
  if (acceptedDataKeys.length > maxCookieEntries) {
    acceptedDataKeys = acceptedDataKeys.slice(0, maxCookieEntries);
  }
  return acceptedDataKeys.reduce((obj, key) => {
    const acceptedDataObj = obj;
    return { ...acceptedDataObj, [key]: acceptedDataObject[key] };
  }, {} as T);
}

/**
 * @description Used to parse json object which we get it from cookie
 */
export const parseCookieValue = <T = string>(cookieName: string, cookieValue: string): Array<T> => {
  let acceptedEventIdsResponse: Array<T>;
  try {
    acceptedEventIdsResponse = cookieValue ? JSON.parse(cookieValue) : [];
  } catch (jsonParseError) {
    // Error parsing cookie value, clear it to be safe
    Cookie.remove(cookieName);
    acceptedEventIdsResponse = [];
  }
  return acceptedEventIdsResponse;
};

/**
 * @description Used to parse locale json object which we get it from cookie
 */
export const parseCookieLocaleValue = <LocaleCookie>(cookieName: string, cookieValue: string): LocaleCookie => {
  let acceptedEventIdsResponseObject;
  try {
    acceptedEventIdsResponseObject = cookieValue ? JSON.parse(cookieValue) : {};
    if (acceptedEventIdsResponseObject?.length) {
      acceptedEventIdsResponseObject = convertArrayIntoMap<LocaleCookie>(acceptedEventIdsResponseObject);
    }
  } catch (jsonParseError) {
    // Error parsing cookie value, clear it to be safe
    Cookie.remove(cookieName);
    acceptedEventIdsResponseObject = {} as LocaleCookie;
  }
  return acceptedEventIdsResponseObject;
};

/**
 * @description Takes an array of cookie key strings, adds them to their localStorage
 * equivalent (note: local storage does not have an expiration date), then deletes
 * the cookie from the user browser.
 *
 * Note: convertCookiesToLocalStorage usage for cookie consent can be removed after October 2023
 * a little over a year after our last cookie ttl
 */
export const convertCookiesToLocalStorage = (
  cookieKeys: string[] = [],
  localStorageUpdater: (value: unknown) => void = undefined
): void => {
  const cookies: Record<string, string> = Cookie.get() || {};

  cookieKeys.forEach(cookieKey => {
    const cookieValue: string = cookies[cookieKey];
    if (cookieValue) {
      try {
        if (localStorageUpdater === undefined && typeof window !== 'undefined') {
          localStorage.setItem(cookieKey, cookieValue);
          Cookie.remove(cookieKey);
        } else if (localStorageUpdater !== undefined) {
          localStorageUpdater(JSON.parse(cookieValue));
          Cookie.remove(cookieKey);
        }
      } catch (parseException) {
        CONVERT_COOKIES_TO_LOCAL_STORAGE_LOG.warn('Unable to convert cookies to local storage value', parseException);
      }
    }
  });
};

export const hasAttendeeAgreedToConsent = (
  eventId: string,
  eventIdArray: string[] = [],
  cookieKeyArray: string[] = []
): boolean => {
  if (Array.isArray(eventIdArray) && eventIdArray.includes(eventId)) {
    return true;
  }

  return cookieKeyArray.some(key => {
    try {
      if (typeof window !== 'undefined' && JSON.parse(localStorage.getItem(key) || '[]').includes(eventId)) {
        return true;
      }
    } catch (parseException) {
      SHOULD_RENDER_COOKIE_BANNER_LOG.warn('Unable to parse local stored value', parseException);
    }
    return false;
  });
};
